const SELECTORS = {
  slot: '[data-illmn-cart-upsell-slot]',
  results: '[data-illmn-cart-upsell-results]',
  status: '[data-illmn-bundle-status]',
  section: '.illmn-cart-upsell',
  card: '.illmn-cart-upsell__card',
};

const MAX_RETRY_ATTEMPTS = 2;
const RETRY_DELAY_MS = 700;
const REQUEST_TIMEOUT_MS = 8000;

function hashCartSignature(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16777619);
  }
  return (hash >>> 0).toString(36);
}

class IllmnCartUpsell extends HTMLElement {
  static observedAttributes = ['data-cart-signature', 'data-product-id', 'data-recommendations-state'];

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  #connected = false;
  #requestSequence = 0;
  #reloadQueued = false;
  #retryAttempt = 0;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #retryTimer = null;

  connectedCallback() {
    this.#connected = true;
    this.#observeVisibility();
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#abortController?.abort();
    this.#intersectionObserver?.disconnect();
    this.#cancelRetry();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.#connected || oldValue === null || oldValue === newValue) return;

    const serverReset = name === 'data-recommendations-state' && newValue === 'idle';
    const cartChanged = name === 'data-cart-signature' || name === 'data-product-id';
    if (!serverReset && !cartChanged) return;

    this.#cancelRetry();
    this.#retryAttempt = 0;

    if (this.#reloadQueued) return;
    this.#reloadQueued = true;
    queueMicrotask(() => {
      this.#reloadQueued = false;
      if (!this.isConnected) return;
      this.#load();
    });
  }

  #observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '200px 0px' }
    );
    this.#intersectionObserver.observe(this);
  }

  async #load() {
    this.#retryTimer = null;
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const baseUrl = this.dataset.url;
    const cartSignature = this.dataset.cartSignature || '';
    const limit = Number.parseInt(this.dataset.limit || '2', 10);
    if (!productId || !sectionId || !baseUrl) {
      this.#hideRecommendations();
      return;
    }

    const sequence = ++this.#requestSequence;
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    this.hidden = false;
    this.closest(SELECTORS.section)?.removeAttribute('hidden');
    this.dataset.recommendationsState = 'loading';
    this.setAttribute('aria-busy', 'true');

    try {
      let results = await this.#fetchResults({
        baseUrl,
        productId,
        sectionId,
        limit,
        intent: 'complementary',
        cartSignature,
        signal: controller.signal,
      });
      if (results.count < limit) {
        try {
          const relatedResults = await this.#fetchResults({
            baseUrl,
            productId,
            sectionId,
            limit,
            intent: 'related',
            cartSignature,
            signal: controller.signal,
          });
          results = this.#mergeResults(results, relatedResults, limit);
        } catch (error) {
          if (error?.name === 'AbortError') throw error;
          if (results.count === 0) throw error;
        }
      }

      if (sequence !== this.#requestSequence || !this.isConnected) return;
      if (results.count === 0 || !results.element) {
        this.#hideRecommendations();
        return;
      }

      const slot = this.querySelector(SELECTORS.slot);
      if (!slot) {
        this.#hideRecommendations();
        return;
      }

      const fragment = document.createDocumentFragment();
      while (results.element.firstChild) fragment.append(results.element.firstChild);
      slot.replaceChildren(fragment);
      this.#retryAttempt = 0;
      this.dataset.recommendationsState = 'loaded';
      this.setAttribute('aria-busy', 'false');
      this.#syncDrawerStickyState();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (sequence !== this.#requestSequence || !this.isConnected) return;

      if (this.#retryAttempt < MAX_RETRY_ATTEMPTS) {
        const delay = RETRY_DELAY_MS * 2 ** this.#retryAttempt;
        this.#retryAttempt += 1;
        this.dataset.recommendationsState = 'retrying';
        this.setAttribute('aria-busy', 'true');
        this.#retryTimer = setTimeout(() => {
          if (this.isConnected) this.#load();
        }, delay);
        return;
      }

      this.#retryAttempt = 0;
      this.#hideRecommendations();
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  #mergeResults(primary, fallback, limit) {
    if (!primary.element) return fallback;
    if (!fallback.element || primary.count >= limit) return primary;

    const cards = Array.from(primary.element.children).filter((child) => child.matches(SELECTORS.card));
    const productIds = new Set(cards.map((card) => card.dataset.productId).filter(Boolean));

    const fallbackCards = Array.from(fallback.element.children).filter((child) => child.matches(SELECTORS.card));
    for (const card of fallbackCards) {
      if (cards.length >= limit) break;

      const productId = card.dataset.productId;
      if (productId && productIds.has(productId)) continue;

      primary.element.append(card);
      cards.push(card);
      if (productId) productIds.add(productId);
    }

    return { element: primary.element, count: cards.length };
  }

  async #fetchResults({ baseUrl, productId, sectionId, limit, intent, cartSignature, signal }) {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('product_id', productId);
    const requestLimit = Math.max(4, Math.min(10, limit * 4));
    url.searchParams.set('limit', String(requestLimit));
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('intent', intent);
    url.searchParams.set('_illmn_cart', `${cartSignature.length}-${hashCartSignature(cartSignature)}`);

    const requestController = new AbortController();
    const abortRequest = () => requestController.abort();
    if (signal.aborted) abortRequest();
    else signal.addEventListener('abort', abortRequest, { once: true });

    const timeout = setTimeout(abortRequest, REQUEST_TIMEOUT_MS);
    let response;
    try {
      response = await fetch(url.toString(), {
        signal: requestController.signal,
        cache: 'no-store',
        headers: { Accept: 'text/html' },
      });
    } catch (error) {
      if (signal.aborted) {
        const abortError = new Error('Recommendation request aborted');
        abortError.name = 'AbortError';
        throw abortError;
      }
      if (requestController.signal.aborted) throw new Error('Recommendation request timed out');
      throw error;
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', abortRequest);
    }

    if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const element = documentFragment.querySelector(SELECTORS.results);
    if (!element) return { element: null, count: 0 };

    const cards = Array.from(element.children).filter((child) => child.matches(SELECTORS.card));
    const productIds = new Set();
    const uniqueCards = [];
    for (const card of cards) {
      const productId = card.dataset.productId;
      if (productId && productIds.has(productId)) {
        card.remove();
        continue;
      }

      if (productId) productIds.add(productId);
      uniqueCards.push(card);
    }

    for (const extraCard of uniqueCards.slice(limit)) extraCard.remove();

    return { element, count: Math.min(uniqueCards.length, limit) };
  }

  #hideRecommendations() {
    this.dataset.recommendationsState = 'empty';
    this.setAttribute('aria-busy', 'false');
    this.hidden = true;

    const section = this.closest(SELECTORS.section);
    if (section && !section.querySelector(SELECTORS.status)) section.hidden = true;
    this.#syncDrawerStickyState();
  }

  #cancelRetry() {
    if (this.#retryTimer === null) return;
    clearTimeout(this.#retryTimer);
    this.#retryTimer = null;
  }

  #syncDrawerStickyState() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      const dialog = this.closest('dialog');
      const summary = dialog?.querySelector('.cart-drawer__summary');
      if (!dialog || !summary) return;

      const drawerHeight = dialog.getBoundingClientRect().height;
      const summaryHeight = summary.getBoundingClientRect().height;
      if (drawerHeight === 0) return;

      dialog.setAttribute('cart-summary-sticky', summaryHeight / drawerHeight > 0.5 ? 'false' : 'true');
    });
  }
}

if (!customElements.get('illmn-cart-upsell')) {
  customElements.define('illmn-cart-upsell', IllmnCartUpsell);
}
